import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  Paper,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Button,
  Box,
  Chip,
  FormGroup,
  Checkbox,
  Divider,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Collapse,
  IconButton,
} from '@material-ui/core';
import {
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Sync as SyncIcon,
  Notifications as NotificationsIcon,
  AutomationOutlined as AutomationIcon,
  MessageOutlined as MessageIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CloudDone as CloudDoneIcon
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';

import { AuthContext } from '../../context/Auth/AuthContext';
import api from '../../services/api';
import toastUtils from '../../utils/toastUtils';
import { i18n } from '../../translate/i18n';

const useStyles = makeStyles((theme) => ({
  settingContainer: {
    padding: '20px',
    border: '1px solid',
    borderColor: theme.palette.type === 'dark' ? '#444' : '#e0e0e0',
    borderRadius: '8px',
    backgroundColor: theme.palette.type === 'dark' ? '#252525' : '#ffffff',
    transition: 'all 0.2s',
    marginBottom: '16px'
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: '8px'
  },
  sectionIcon: {
    marginRight: '8px',
    color: theme.palette.primary.main,
  },
  formControl: {
    minWidth: 200,
    marginBottom: theme.spacing(2),
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  chip: {
    margin: theme.spacing(0.5),
  },
  testButton: {
    marginTop: theme.spacing(2),
  },
  configCard: {
    marginTop: theme.spacing(2),
  },
  statusChip: {
    fontSize: '0.75rem',
    height: '20px',
    marginLeft: theme.spacing(0.5)
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 200,
  },
}));

const validationSchema = Yup.object().shape({
  integrationToken: Yup.string().required(i18n.t('napp.validation.apiKeyRequired')),
  environment: Yup.string().required(i18n.t('napp.validation.environmentRequired')),
  syncInterval: Yup.number().min(1).max(60).required(i18n.t('napp.validation.intervalRequired')),
  feedLimit: Yup.number().min(1).max(20).required('Limite do feed é obrigatório'),
});

const NappConfig = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [config, setConfig] = useState(null);
  const [configOptions, setConfigOptions] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testingConnection, setTestingConnection] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    status: true,
    basic: true,
    notifications: false,
    automation: false,
    templates: false
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      const [configRes, optionsRes, statusRes, statsRes] = await Promise.all([
        api.get('/napp/config'),
        api.get('/napp/config-options'),
        api.get('/napp/sync-status'),
        api.get('/napp/integration-stats'),
      ]);

      setConfig(configRes.data);
      setConfigOptions(optionsRes.data);
      setSyncStatus(statusRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
      toastUtils.error(i18n.t('napp.messages.loadError'));
    } finally {
      setLoading(false);
    }
  }, []);

  const toggleSection = useCallback((section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  }, []);

  const handleTestConnection = useCallback(async (values) => {
    try {
      setTestingConnection(true);
      const response = await api.post('/napp/test-connection', {
        integrationToken: values.integrationToken,
        environment: values.environment,
      });

      if (response.data.success) {
        toastUtils.success(i18n.t('napp.messages.testSuccess'));
      } else {
        toastUtils.error(response.data.message || i18n.t('napp.messages.testError'));
      }
    } catch (error) {
      toastUtils.error(i18n.t('napp.messages.testError') + ': ' + (error.response?.data?.message || error.message));
    } finally {
      setTestingConnection(false);
    }
  }, []);

  const handleSaveConfig = useCallback(async (values) => {
    try {
      setSaving(true);
      await api.put('/napp/config', values);
      toastUtils.success(i18n.t('napp.messages.saveSuccess'));
      loadInitialData();
    } catch (error) {
      toastUtils.error(i18n.t('napp.messages.saveError') + ': ' + (error.response?.data?.message || error.message));
    } finally {
      setSaving(false);
    }
  }, [loadInitialData]);

  const handleResetSyncErrors = useCallback(async () => {
    try {
      await api.post('/napp/reset-sync-errors');
      toastUtils.success(i18n.t('napp.messages.resetSyncSuccess'));
      loadInitialData();
    } catch (error) {
      toastUtils.error(i18n.t('napp.messages.resetSyncError') + ': ' + (error.response?.data?.message || error.message));
    }
  }, [loadInitialData]);

  // Hooks devem sempre vir antes de qualquer return condicional
  const getStatusColor = useCallback((status) => {
    switch (status) {
      case 'healthy': return 'primary';
      case 'warning': return 'secondary';
      case 'error': return 'default';
      default: return 'default';
    }
  }, []);

  const getStatusLabel = useCallback((status) => {
    switch (status) {
      case 'healthy': return i18n.t('napp.status.healthy');
      case 'warning': return i18n.t('napp.status.warning');
      case 'error': return i18n.t('napp.status.error');
      default: return i18n.t('napp.status.unknown');
    }
  }, []);

  const getStatusIcon = useCallback((status) => {
    switch (status) {
      case 'healthy': return <CheckCircleIcon />;
      case 'warning': return <WarningIcon />;
      case 'error': return <ErrorIcon />;
      default: return <ErrorIcon />;
    }
  }, []);

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box className={classes.settingContainer}>
        <Box className={classes.sectionHeader}>
          <SettingsIcon className={classes.sectionIcon} />
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            {i18n.t('napp.title')}
          </Typography>
        </Box>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: '16px' }}>
          {i18n.t('napp.subtitle')}
        </Typography>

        {config && (
          <Grid container spacing={1}>
            <Grid item>
              <Chip
                icon={config.active ? <CheckCircleIcon /> : <ErrorIcon />}
                label={config.active ? i18n.t('napp.status.active') : i18n.t('napp.status.inactive')}
                color={config.active ? 'primary' : 'default'}
                variant={config.active ? 'default' : 'outlined'}
                className={classes.statusChip}
              />
            </Grid>
            {syncStatus && (
              <Grid item>
                <Chip
                  icon={getStatusIcon(syncStatus.syncHealthStatus)}
                  label={getStatusLabel(syncStatus.syncHealthStatus)}
                  color={getStatusColor(syncStatus.syncHealthStatus)}
                  className={classes.statusChip}
                />
              </Grid>
            )}
            {stats && (
              <Grid item>
                <Chip
                  icon={<SyncIcon />}
                  label={`${stats.totalSyncedOrders} ${i18n.t('napp.status.syncedOrders')}`}
                  color="default"
                  variant="outlined"
                  className={classes.statusChip}
                />
              </Grid>
            )}
          </Grid>
        )}
      </Box>

      {/* Status da Integração */}
      {config && (
        <Card style={{ marginBottom: '16px' }}>
          <CardHeader
            avatar={<CloudDoneIcon />}
            title={i18n.t('napp.sections.integrationStatus')}
            action={
              <IconButton onClick={() => toggleSection('status')}>
                {expandedSections.status ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            }
          />
          <Collapse in={expandedSections.status}>
            <CardContent>
              <Grid container spacing={2}>
                {stats && (
                  <>
                    <Grid item xs={6} md={3}>
                      <Typography variant="h6">{stats.totalSyncedOrders}</Typography>
                      <Typography variant="caption">{i18n.t('napp.stats.syncedOrders')}</Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="h6">{stats.failedSyncs}</Typography>
                      <Typography variant="caption">{i18n.t('napp.stats.failedSyncs')}</Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="h6">{stats.successRate}%</Typography>
                      <Typography variant="caption">{i18n.t('napp.stats.successRate')}</Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="h6">
                        {stats.lastSyncAt
                          ? new Date(stats.lastSyncAt).toLocaleString('pt-BR')
                          : i18n.t('napp.stats.never')
                        }
                      </Typography>
                      <Typography variant="caption">{i18n.t('napp.stats.lastSync')}</Typography>
                    </Grid>
                  </>
                )}
              </Grid>

              {syncStatus?.failedSyncs > 0 && (
                <Box mt={2}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleResetSyncErrors}
                    startIcon={<SyncIcon />}
                  >
                    {i18n.t('napp.buttons.resetSyncErrors')}
                  </Button>
                </Box>
              )}
            </CardContent>
          </Collapse>
        </Card>
      )}

      {/* Formulário de Configuração */}
      <Card style={{ marginBottom: '16px' }}>
        <CardHeader
          avatar={<SettingsIcon />}
          title={i18n.t('napp.sections.basicConfiguration')}
          action={
            <IconButton onClick={() => toggleSection('basic')}>
              {expandedSections.basic ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          }
        />
        <Collapse in={expandedSections.basic}>
          <CardContent>
            <Formik
              initialValues={{
                integrationToken: config?.integrationToken || '',
                environment: config?.environment || 'staging',
                syncInterval: config?.syncInterval || 5,
                enableNotifications: config?.enableNotifications || true,
                notificationStatuses: config?.notificationStatuses || [],
                webhookUrl: config?.webhookUrl || '',
                feedLimit: config?.feedLimit || 20,
                autoCreateContacts: config?.autoCreateContacts || true,
                autoCreateTickets: config?.autoCreateTickets || false,
                ticketCreationStatuses: config?.ticketCreationStatuses || [],
                active: config?.active || false,
              }}
              validationSchema={validationSchema}
              onSubmit={handleSaveConfig}
              enableReinitialize
            >
              {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => (
                <Form id="napp-config-form" autoComplete="on">
                  <div className={classes.section}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Field
                          as={TextField}
                          fullWidth
                          name="integrationToken"
                          label={i18n.t('napp.connection.apiKey')}
                          type="password"
                          value={values.integrationToken}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.integrationToken && !!errors.integrationToken}
                          helperText={touched.integrationToken && errors.integrationToken}
                          placeholder={i18n.t('napp.connection.apiKeyHelper')}
                          autoComplete="current-password"
                          form="napp-config-form"
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth className={classes.formControl}>
                          <InputLabel>{i18n.t('napp.connection.environment')}</InputLabel>
                          <Select
                            name="environment"
                            value={values.environment}
                            onChange={handleChange}
                          >
                            {configOptions?.environments?.map((env) => (
                              <MenuItem key={env.value} value={env.value}>
                                {env.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth className={classes.formControl}>
                          <InputLabel>{i18n.t('napp.connection.syncInterval')}</InputLabel>
                          <Select
                            name="syncInterval"
                            value={values.syncInterval}
                            onChange={handleChange}
                          >
                            {configOptions?.syncIntervalOptions?.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          name="feedLimit"
                          label="Limite do Feed"
                          type="number"
                          value={values.feedLimit}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.feedLimit && !!errors.feedLimit}
                          helperText={touched.feedLimit && errors.feedLimit}
                          inputProps={{ min: 1, max: 20 }}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          name="webhookUrl"
                          label="URL do Webhook (Opcional)"
                          value={values.webhookUrl}
                          onChange={handleChange}
                          placeholder="https://seudominio.com/webhook/napp"
                          helperText="URL do seu servidor onde a NAPP enviará notificações automáticas de mudanças de status (complementar ao polling automático)"
                        />
                      </Grid>
                    </Grid>

                    <Box mt={2}>
                      <Button
                        variant="outlined"
                        onClick={() => handleTestConnection(values)}
                        disabled={testingConnection || !values.integrationToken}
                        className={classes.testButton}
                      >
                        {testingConnection ? <CircularProgress size={24} /> : i18n.t('napp.connection.testConnection')}
                      </Button>
                    </Box>
                  </div>
                </Form>
              )}
            </Formik>
          </CardContent>
        </Collapse>
      </Card>

      {/* Configurações de Notificações */}
      <Card style={{ marginBottom: '16px' }}>
        <CardHeader
          avatar={<NotificationsIcon />}
          title={i18n.t('napp.sections.notifications')}
          action={
            <IconButton onClick={() => toggleSection('notifications')}>
              {expandedSections.notifications ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          }
        />
        <Collapse in={expandedSections.notifications}>
          <CardContent>
            <Formik
              initialValues={{
                integrationToken: config?.integrationToken || '',
                environment: config?.environment || 'staging',
                syncInterval: config?.syncInterval || 5,
                enableNotifications: config?.enableNotifications || true,
                notificationStatuses: config?.notificationStatuses || [],
                webhookUrl: config?.webhookUrl || '',
                feedLimit: config?.feedLimit || 20,
                autoCreateContacts: config?.autoCreateContacts || true,
                autoCreateTickets: config?.autoCreateTickets || false,
                ticketCreationStatuses: config?.ticketCreationStatuses || [],
                active: config?.active || false,
              }}
              validationSchema={validationSchema}
              onSubmit={handleSaveConfig}
              enableReinitialize
            >
              {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => (
                <Form id="napp-notifications-form" autoComplete="on">
                  <div className={classes.section}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.enableNotifications}
                          onChange={(e) => setFieldValue('enableNotifications', e.target.checked)}
                        />
                      }
                      label="Habilitar notificações WhatsApp"
                    />

                    {values.enableNotifications && (
                      <Box mt={2}>
                        <Typography variant="subtitle2" gutterBottom>
                          Status para Notificação:
                        </Typography>
                        <FormGroup row>
                          {configOptions?.notificationStatuses?.map((status) => (
                            <FormControlLabel
                              key={status.value}
                              control={
                                <Checkbox
                                  checked={values.notificationStatuses.includes(status.value)}
                                  onChange={(e) => {
                                    const newStatuses = e.target.checked
                                      ? [...values.notificationStatuses, status.value]
                                      : values.notificationStatuses.filter(s => s !== status.value);
                                    setFieldValue('notificationStatuses', newStatuses);
                                  }}
                                />
                              }
                              label={status.label}
                            />
                          ))}
                        </FormGroup>
                      </Box>
                    )}
                  </div>

                  <Divider />

                  <div className={classes.section}>
                    <Typography variant="h6" gutterBottom>
                      Automações
                    </Typography>

                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.autoCreateContacts}
                          onChange={(e) => setFieldValue('autoCreateContacts', e.target.checked)}
                        />
                      }
                      label="Criar contatos automaticamente"
                    />

                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.autoCreateTickets}
                          onChange={(e) => setFieldValue('autoCreateTickets', e.target.checked)}
                        />
                      }
                      label="Criar tickets automaticamente"
                    />

                    {values.autoCreateTickets && (
                      <Box mt={2}>
                        <Typography variant="subtitle2" gutterBottom>
                          Status para Criação de Tickets:
                        </Typography>
                        <FormGroup row>
                          {configOptions?.ticketCreationStatuses?.map((status) => (
                            <FormControlLabel
                              key={status.value}
                              control={
                                <Checkbox
                                  checked={values.ticketCreationStatuses.includes(status.value)}
                                  onChange={(e) => {
                                    const newStatuses = e.target.checked
                                      ? [...values.ticketCreationStatuses, status.value]
                                      : values.ticketCreationStatuses.filter(s => s !== status.value);
                                    setFieldValue('ticketCreationStatuses', newStatuses);
                                  }}
                                />
                              }
                              label={status.label}
                            />
                          ))}
                        </FormGroup>
                      </Box>
                    )}
                  </div>

                  <Divider />

                  {/* Seção de Templates de Notificação */}
                  <div className={classes.section}>
                    <Typography variant="h6" gutterBottom>
                      Templates de Notificação WhatsApp
                    </Typography>
                    <Typography variant="body2" color="textSecondary" paragraph>
                      Personalize as mensagens automáticas enviadas para cada status do pedido
                    </Typography>

                    {values.enableNotifications && configOptions?.notificationTemplates && (
                      <Grid container spacing={2}>
                        {Object.entries(configOptions.notificationTemplates).map(([statusKey, template]) => (
                          <Grid item xs={12} key={statusKey}>
                            <Card variant="outlined">
                              <CardContent>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} md={3}>
                                    <Box display="flex" alignItems="center">
                                      <span style={{ fontSize: '1.2em', marginRight: 8 }}>
                                        {template.emoji}
                                      </span>
                                      <Typography variant="subtitle1">
                                        {template.title}
                                      </Typography>
                                    </Box>
                                    <Typography variant="caption" color="textSecondary">
                                      Status: {statusKey}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12} md={9}>
                                    <TextField
                                      fullWidth
                                      multiline
                                      rows={4}
                                      name={`template_${statusKey}`}
                                      label="Mensagem do Template"
                                      value={values[`template_${statusKey}`] || template.message}
                                      onChange={handleChange}
                                      placeholder={template.message}
                                      helperText="Use {{customer.name}}, {{order.code}}, {{order.totalAmount}} para variáveis dinâmicas"
                                    />
                                  </Grid>
                                </Grid>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    )}

                    {!values.enableNotifications && (
                      <Paper style={{ padding: 16, backgroundColor: '#e3f2fd', marginTop: 16 }}>
                        <Typography variant="body2" color="textSecondary">
                          📝 Habilite as notificações WhatsApp para configurar os templates de mensagem
                        </Typography>
                      </Paper>
                    )}
                  </div>

                  <Divider />

                  <Box mt={3} display="flex" alignItems="center" justifyContent="space-between">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.active}
                          onChange={(e) => setFieldValue('active', e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Ativar Integração NAPP"
                    />

                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={saving}
                      size="large"
                    >
                      {saving ? <CircularProgress size={24} /> : i18n.t('napp.buttons.save')}
                    </Button>
                  </Box>
                </Form>
              )}
            </Formik>
          </CardContent>
        </Collapse>
      </Card>
    </Box>
  );
};

export default NappConfig;